import { useState } from "react";
import { Upload, Button, message, List, Typography, Popconfirm } from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  FileOutlined,
  FileImageOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import type { Attachment } from "../../../shared/types";
import { api } from "../../services/api";

const { Text } = Typography;

interface FileUploadProps {
  caseId?: number;
  category: string;
  value?: Attachment[];
  onChange?: (files: Attachment[]) => void;
  maxCount?: number;
  disabled?: boolean;
}

export default function FileUpload({
  caseId,
  category,
  value = [],
  onChange,
  maxCount = 5,
  disabled = false,
}: FileUploadProps) {
  const [uploading, setUploading] = useState(false);

  const getFileIcon = (fileType: string) => {
    if (fileType?.startsWith("image/")) {
      return <FileImageOutlined style={{ color: "#1890ff" }} />;
    }
    if (fileType?.includes("pdf")) {
      return <FilePdfOutlined style={{ color: "#ff4d4f" }} />;
    }
    return <FileOutlined style={{ color: "#666" }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const handleUpload = async (file: File) => {
    if (!caseId) {
      message.error("请先保存案件");
      return false;
    }

    setUploading(true);
    try {
      const result = await api.uploadFile(caseId, file, category);
      const newAttachment: Attachment = {
        id: result.id,
        case_id: caseId,
        record_id: null,
        file_name: result.fileName,
        file_key: result.fileKey,
        file_size: file.size,
        file_type: file.type,
        category,
        uploaded_by: 0,
        uploader_name: "",
        created_at: new Date().toISOString(),
      };
      onChange?.([...value, newAttachment]);
      message.success("上传成功");
    } catch (error: any) {
      message.error(error.message || "上传失败");
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleDelete = async (id: number) => {
    try {
      await api.deleteFile(id);
      onChange?.(value.filter((f) => f.id !== id));
      message.success("删除成功");
    } catch (error: any) {
      message.error(error.message || "删除失败");
    }
  };

  return (
    <div>
      <Upload
        beforeUpload={handleUpload}
        showUploadList={false}
        disabled={disabled || uploading}
        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx"
      >
        <Button
          icon={<UploadOutlined />}
          loading={uploading}
          disabled={disabled || value.length >= maxCount}
        >
          上传文件 ({value.length}/{maxCount})
        </Button>
      </Upload>

      {value.length > 0 && (
        <List
          size="small"
          className="mt-2"
          dataSource={value}
          renderItem={(item) => (
            <List.Item
              actions={
                !disabled
                  ? [
                      <Popconfirm
                        key="delete"
                        title="确定删除此文件?"
                        onConfirm={() => handleDelete(item.id)}
                      >
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>,
                    ]
                  : []
              }
            >
              <List.Item.Meta
                avatar={getFileIcon(item.file_type)}
                title={
                  <Text className="text-sm" ellipsis>
                    {item.file_name}
                  </Text>
                }
                description={
                  <Text type="secondary" className="text-xs">
                    {formatFileSize(item.file_size)}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );
}
